<template>
  <div
    class="right-side-panel"
    :class="{
      'dialog-opened': isDialogOpenedFromRightPanel,
      'static-bg': !isOperatorDashboard,
      'allowed-hover-expand': isAllowExpandingOnHover,
    }"
  >
    <div
      v-if="isShowRegFlow"
      class="logo reg-flow-header"
      :class="isOperatorDashboard ? rightPanelClass : ''"
    >
      <h5>Recommended Reg Flow</h5>

      <div>
        <p-button
          round
          type="yellow"
          @click="isShowRegFlow = false"
        >
          <i class="fa fa-arrow-left" />
          <span> Back</span>
        </p-button>

        <p-button
          class="circle-button back-top"
          round
          @click="goToTop"
        >
          <i class="fa fa-arrow-up" />
        </p-button>
      </div>
    </div>
    <div
      v-else
      class="logo"
      :class="isOperatorDashboard ? rightPanelClass : ''"
    >
      <p-button
        v-if="!isTrainingOperator"
        class="circle-button toggle-right-panel"
        round
        :type="isBlacklisted || isFraudCustomer ? 'warning' : 'danger'"
        @click="toggleRightSidebar"
      >
        <i class="fa fa-chevron-right" />
        <i class="fa fa-chevron-left" />
      </p-button>

      <div class="navigation-button-group">
        <router-link
          v-if="!isFamilyDashboard"
          :to="{ path: '/operator' }"
        >
          <el-tooltip placement="bottom">
            <template #content>
              Dashboard
            </template>
            <a class="navigation-button dashboard-icon">
              <i class="fa fa-dashboard" />
            </a>
          </el-tooltip>
        </router-link>

        <template v-if="(isOperatorDashboard || isPartnerDashboard) && customerInfo.id">
          <el-tooltip placement="bottom">
            <template #content>
              Plan Info
            </template>
            <a
              href="#planInfo"
              class="navigation-button plan-icon"
            ><i class="fa fa-trophy" /></a>
          </el-tooltip>

          <el-tooltip placement="bottom">
            <template #content>
              Credit Card Info
            </template>
            <a
              href="#creditCardInfo"
              class="navigation-button credit-card-icon"
            ><i class="fa fa-credit-card" /></a>
          </el-tooltip>

          <el-tooltip placement="bottom">
            <template #content>
              Family Members Info
            </template>
            <a
              href="#addFamilyMember"
              class="navigation-button family-member-icon"
            ><i class="fa fa-heart-o" /></a>
          </el-tooltip>

          <el-tooltip placement="bottom">
            <template #content>
              Custom Locations
            </template>
            <a
              href="#customLocationSection3"
              class="navigation-button custom-location-icon"
            ><i class="fa fa-map" /></a>
          </el-tooltip>

          <el-tooltip placement="bottom">
            <template #content>
              Last Ride Info
            </template>
            <a
              href="#lastRide"
              class="navigation-button last-ride-icon"
            ><i class="fa fa-car" /></a>
          </el-tooltip>
        </template>

        <div
          v-if="recRegFlowSrc && isOperatorDashboard"
          class="flex-fill text-left"
        >
          <el-tooltip placement="bottom">
            <template #content>
              Show Registration Flow
            </template>
            <a
              href="#"
              class="navigation-button reg-flow-icon"
              @click="isShowRegFlow = !isShowRegFlow"
            ><i class="fa fa-book" /></a>
          </el-tooltip>
        </div>
      </div>

      <p-button
        v-if="isOperatorDashboard || isPartnerDashboard"
        class="customer-reset"
        round
        type="yellow"
        @click="resetRightPanel"
      >
        Reset
      </p-button>

      <p-button
        v-if="isOperatorDashboard || isPartnerDashboard"
        class="circle-button back-top"
        round
        :type="isBlacklisted || isFraudCustomer ? 'warning' : 'danger'"
        @click="goToTop"
      >
        <i class="fa fa-arrow-up" />
      </p-button>
    </div>

    <div
      ref="sidebarScrollArea"
      class="sidebar-wrapper"
      :class="isOperatorDashboard ? rightPanelClass : ''"
    >
      <slot />

      <FamilyRightContent v-if="isFamilyDashboard" />

      <OperatorRightContent
        v-else-if="isOperatorDashboard || isPartnerDashboard"
        :back-to-top="backToTop"
        :is-show-reg-flow="isShowRegFlow"
        :rec-reg-flow-src="recRegFlowSrc"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, toRefs, computed, watch, defineAsyncComponent, getCurrentInstance } from 'vue';
import events from '@/events';

import { useCustomer } from '@/use/customer';
import { useDashboard } from '@/use/app';
import { usePartner } from '@/use/partner';
import { useGlobal } from '@/use/global';
import { useProfile } from '@/use/profile';
import { meOperator } from '@/services/operators/viewModel';
import { lastCallLog } from '@/services/callLogs/callLogsViewModel';

const OperatorRightContent = defineAsyncComponent(() => import('@/components/Main/RightPanelContent.vue') as any);

const FamilyRightContent = defineAsyncComponent(() => import('@/components/Main/FamilyRightPanelContent.vue') as any);

defineProps({
  type: {
    type: String,
    default: 'sidebar',
    validator: (value: string) => {
      return ['sidebar', 'navbar'].indexOf(value) !== -1;
    },
  },
});

const isShowRegFlow = ref(false);
const { userRoles } = useProfile();
const { isTrainingOperator, isHomeServicesOperator } = toRefs(userRoles);
const { customerInfo, customerStatus, customerPlans } = useCustomer();
const {
  isPartnerCustomer,
  isMembershipExpired,
  isNeverHadMembership,
  isMembershipSuspended,
  isFraudCustomer,
  isNeedsToClearDisputesCustomer,
  isBlacklisted,
  isUnpaidCustomer,
  isActiveCustomer,
  isInFirstMonthOfHardRegister,
} = toRefs(customerStatus);
const { isTrialPlan } = toRefs(customerPlans);
const { isOperatorDashboard, isFamilyDashboard, isPartnerDashboard } = useDashboard();
const { partnerId } = usePartner();
const { isDialogOpenedFromRightPanel, isAllowExpandingOnHover } = useGlobal();

const backToTop = ref(false);

const instance: any = getCurrentInstance();

const rightPanelClass = computed(() => {
  if (isFamilyDashboard.value || !customerInfo.value.id) {
    return '';
  }

  // Closed
  if (!isActiveCustomer.value) {
    return 'inactive-account';
  }

  // Fraud
  if (isBlacklisted.value || isFraudCustomer.value) {
    return 'fraud-account';
  }

  // Soft registered account
  if (isNeverHadMembership.value) {
    return 'never-had-membership-account';
  }

  // On an active trial
  if (isTrialPlan.value) {
    return 'trial-plan-account';
  }

  // Suspended Partnership/Enterprise - Check this BEFORE regular partnership
  if (isPartnerCustomer.value && isMembershipSuspended.value) {
    return 'suspended-partnership-account';
  }

  // Outstanding balance
  if (isUnpaidCustomer.value) {
    return 'unpaid-rides-account';
  }

  // Partnership/Enterprise
  if (isPartnerCustomer.value) {
    return 'partnership-account';
  }

  // In their first month (these are new hard reg clients)
  if (isInFirstMonthOfHardRegister.value) {
    return 'first-month-hard-registered-account';
  }

  // Expired membership
  if (isMembershipExpired.value) {
    return 'membership-expired-account';
  }

  // Suspended membership
  if (isMembershipSuspended.value) {
    return 'membership-suspended-account';
  }

  // Unresolved disputes
  if (isNeedsToClearDisputesCustomer.value) {
    return 'clear-dispute-account';
  }

  return '';
});

const recRegFlowSrc = computed(() => {
  if (!meOperator.value?.isEnableRegFlowView) {
    return null;
  }

  if (isHomeServicesOperator.value) {
    return 'https://docs.google.com/document/d/e/2PACX-1vQcXC6doZMa8iCroQybbJIkmcCfJYjk8RGy4E_8P1brEqlbW8xrnySLJtGZmgZ8iwUsK1plTGN8P97h/pub?embedded=true'
  }

  return (
    'https://docs.google.com/document/d/e/2PACX-1vQ5GMHpJyPoiNtQfTjNlgX5JrAYO7fnZVbe8uLdFq1k34YWsiAb2_sG0z4-qvwGFOO5REJWj3Bx0FVA/pub?embedded=true&callLogId=' +
    lastCallLog.value?.id
  );
});

const toggleRightSidebar = () => {
  instance.data.panelStore.toggleMinimize();
};

const goToTop = () => {
  backToTop.value = !backToTop.value;
};

const resetRightPanel = () => {
  events.emit('reset-page');
};

watch(partnerId, (value: any) => {
  if (value && isPartnerDashboard.value) {
    resetRightPanel();
  }
});
</script>

<style lang="scss">
.reg-flow-header {
  h5 {
    margin: 0;
    color: white;
    font-weight: bold;
  }
}
</style>
